/* ==========================================================================
   PWR EVENTS
   Central event bus for the PWR architecture + a bridge to Dawn's pub/sub.
   Loaded AFTER pwr-utils. Registers window.PWR.bus and window.PWR.EVENTS.

   Why a bridge, not a replacement:
     Dawn ships its own publish/subscribe (pubsub.js) and PUB_SUB_EVENTS
     (constants.js) that the cart drawer, notification, and product-info rely
     on. PWR emits on its OWN namespaced bus for PWR-to-PWR communication AND
     mirrors cart-related events onto Dawn's bus so native components keep
     working. Nothing goes deaf.
   ========================================================================== */
(function () {
  'use strict';

  window.PWR = window.PWR || {};
  if (window.PWR.bus) return;

  /** Canonical PWR event names (namespaced to avoid collisions). */
  const EVENTS = {
    // Cart
    cartAdd: 'pwr:cart:add',
    cartAdded: 'pwr:cart:added',
    cartUpdated: 'pwr:cart:updated',
    cartError: 'pwr:cart:error',
    cartOpen: 'pwr:cart:open',
    // Wishlist
    wishlistChanged: 'pwr:wishlist:changed',
    // UI / viewport
    breakpointChange: 'pwr:viewport:breakpoint',
    scroll: 'pwr:viewport:scroll',
    resize: 'pwr:viewport:resize',
    reducedMotionChange: 'pwr:motion:reduced',
    // Section lifecycle (mirrors Shopify Theme Editor)
    sectionLoad: 'pwr:section:load',
    sectionUnload: 'pwr:section:unload',
    sectionSelect: 'pwr:section:select',
    sectionDeselect: 'pwr:section:deselect',
    blockSelect: 'pwr:block:select',
    blockDeselect: 'pwr:block:deselect',
  };

  /**
   * Lightweight event bus. Supports on/once/off/emit, wildcard-free by design
   * (explicit event names only, for predictability and performance).
   */
  class EventBus {
    constructor() {
      this._map = new Map();
    }

    on(event, handler) {
      if (!this._map.has(event)) this._map.set(event, new Set());
      this._map.get(event).add(handler);
      return () => this.off(event, handler);
    }

    once(event, handler) {
      const wrapped = (payload) => {
        this.off(event, wrapped);
        handler(payload);
      };
      return this.on(event, wrapped);
    }

    off(event, handler) {
      const set = this._map.get(event);
      if (!set) return;
      if (handler) {
        set.delete(handler);
      } else {
        set.clear();
      }
    }

    emit(event, payload) {
      const set = this._map.get(event);
      if (!set || set.size === 0) return;
      // Snapshot so handlers can unsubscribe mid-emit safely.
      Array.from(set).forEach((handler) => {
        try {
          handler(payload);
        } catch (err) {
          if (window.console) console.error('[PWR bus] handler error for ' + event, err);
        }
      });
    }
  }

  const bus = new EventBus();

  /* --------------------------------------------------------------------------
     DAWN PUB/SUB BRIDGE
     Dawn's `publish` / `subscribe` are script-scope globals. Access them
     defensively at call time (load order across <script defer> tags means
     they resolve when invoked, not necessarily at parse time).
     -------------------------------------------------------------------------- */
  const dawn = {
    publish(name, data) {
      if (typeof window.publish === 'function') {
        window.publish(name, data);
      } else if (typeof publish === 'function') {
        // eslint-disable-next-line no-undef
        publish(name, data);
      }
    },
    subscribe(name, cb) {
      if (typeof window.subscribe === 'function') {
        return window.subscribe(name, cb);
      }
      if (typeof subscribe === 'function') {
        // eslint-disable-next-line no-undef
        return subscribe(name, cb);
      }
      return function () {};
    },
    events() {
      return (typeof window.PUB_SUB_EVENTS !== 'undefined' && window.PUB_SUB_EVENTS)
        || (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS)
        || {};
    },
  };

  /**
   * Emit a PWR cart event AND mirror it onto Dawn's bus so native cart
   * components (drawer, notification, icon bubble) react. Call this instead
   * of bus.emit for cart-updates that Dawn should also hear.
   */
  function emitCartUpdated(payload) {
    bus.emit(EVENTS.cartUpdated, payload);
    const dawnEvents = dawn.events();
    if (dawnEvents.cartUpdate) {
      dawn.publish(dawnEvents.cartUpdate, {
        source: (payload && payload.source) || 'pwr',
        cartData: payload && payload.cartData,
        variantId: payload && payload.variantId,
      });
    }
  }

  /* --------------------------------------------------------------------------
     SHOPIFY SECTION LIFECYCLE -> PWR bus
     Central place that translates Theme Editor DOM events into PWR events, so
     controllers subscribe to ONE bus rather than each wiring their own
     document listeners.
     -------------------------------------------------------------------------- */
  function wireShopifyLifecycle() {
    const map = [
      ['shopify:section:load', EVENTS.sectionLoad],
      ['shopify:section:unload', EVENTS.sectionUnload],
      ['shopify:section:select', EVENTS.sectionSelect],
      ['shopify:section:deselect', EVENTS.sectionDeselect],
      ['shopify:block:select', EVENTS.blockSelect],
      ['shopify:block:deselect', EVENTS.blockDeselect],
    ];
    map.forEach(([domEvent, pwrEvent]) => {
      document.addEventListener(domEvent, (event) => {
        bus.emit(pwrEvent, {
          sectionId: event.detail && event.detail.sectionId,
          blockId: event.detail && event.detail.blockId,
          target: event.target,
          load: event.detail && event.detail.load,
        });
      });
    });
  }

  wireShopifyLifecycle();

  window.PWR.EVENTS = EVENTS;
  window.PWR.bus = bus;
  window.PWR.dawn = dawn;
  window.PWR.emitCartUpdated = emitCartUpdated;
})();
